import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import {
    REWARD_COLLECTION_TIMING as TIMING,
    rewardCollectionTimeout,
    type RewardCollectionPhase,
    type RewardCollectionReason,
} from "../helpers/rewardCollection";
import {
    REWARD_CURVES,
    sampleRewardCurve,
} from "../helpers/rewardCollectionCurves";

interface UseRewardCollectionTimelineOptions {
    autoCollectAfterMs: number | null;
    rewardCount: number;
    onCollect?: (reason: RewardCollectionReason) => void;
    onComplete: (reason: RewardCollectionReason) => void;
}

/** One visible-page clock drives the decoded curves, countdown, and completion. */
export function useRewardCollectionTimeline({
    autoCollectAfterMs,
    rewardCount,
    onCollect,
    onComplete,
}: UseRewardCollectionTimelineOptions) {
    const bodyRef = useRef<HTMLDivElement>(null);
    const [phase, setPhase] = useState<RewardCollectionPhase>("entering");
    const requestedReason = useRef<RewardCollectionReason | null>(null);
    const completionDelivered = useRef(false);
    const onCollectRef = useRef(onCollect);
    const onCompleteRef = useRef(onComplete);
    useLayoutEffect(() => {
        onCollectRef.current = onCollect;
        onCompleteRef.current = onComplete;
    }, [onCollect, onComplete]);
    const collect = useCallback((reason: RewardCollectionReason) => {
        if (requestedReason.current !== null) return;
        requestedReason.current = reason;
        onCollectRef.current?.(reason);
    }, []);
    const timeout = rewardCollectionTimeout(autoCollectAfterMs);

    useLayoutEffect(() => {
        const body = bodyRef.current;
        const dialog = body?.closest("dialog");
        if (!body || !dialog) return;
        const items = Array.from(
            body.querySelectorAll<HTMLElement>(".reward-collection-item"),
        );
        const reducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        );
        const enteredAt = TIMING.revealDelayMs + TIMING.revealMs;
        const exitDuration =
            TIMING.itemsOutMs +
            Math.max(0, rewardCount - 1) * TIMING.itemStaggerMs;
        let elapsed = 0;
        let previous = performance.now();
        let exitAt: number | null = null;
        let currentPhase: RewardCollectionPhase = "entering";
        let frameId = 0;

        const resetFrameTime = () => {
            previous = performance.now();
        };
        const renderFrame = (now: number) => {
            if (document.visibilityState === "visible")
                elapsed += now - previous;
            previous = now;
            if (timeout !== null && elapsed >= timeout) collect("timeout");
            if (
                requestedReason.current !== null &&
                exitAt === null &&
                elapsed >= enteredAt
            )
                exitAt = elapsed;

            const out = exitAt === null ? 0 : Math.max(0, elapsed - exitAt);
            const alpha =
                exitAt === null
                    ? Math.min(1, elapsed / TIMING.backdropInMs)
                    : 1 -
                      Math.min(
                          1,
                          Math.max(0, out - exitDuration) /
                              TIMING.backdropOutMs,
                      );
            dialog.style.setProperty(
                "--reward-backdrop-opacity",
                String(
                    0.7254902 *
                        (reducedMotion.matches
                            ? out >= exitDuration
                                ? 0
                                : 1
                            : alpha),
                ),
            );
            body.style.setProperty(
                "--reward-countdown",
                String(
                    timeout === null ? 1 : Math.max(0, 1 - elapsed / timeout),
                ),
            );
            body.style.setProperty(
                "--reward-reveal-scale",
                String(
                    reducedMotion.matches
                        ? 1
                        : sampleRewardCurve(
                              REWARD_CURVES.reveal,
                              Math.max(
                                  0,
                                  Math.min(
                                      0.5,
                                      ((elapsed - TIMING.revealDelayMs) /
                                          TIMING.revealMs) *
                                          0.5,
                                  ),
                              ),
                          ),
                ),
            );
            const rayTime = (elapsed / 1000) % 25;
            // Unity's positive Y and rotation axes are opposite CSS's screen axes.
            body.style.setProperty(
                "--reward-ray-angle",
                `${reducedMotion.matches ? 0 : -sampleRewardCurve(REWARD_CURVES.rayRotation, rayTime)}deg`,
            );
            body.style.setProperty(
                "--reward-ray-scale",
                String(
                    reducedMotion.matches
                        ? 1
                        : sampleRewardCurve(REWARD_CURVES.rayScale, rayTime),
                ),
            );
            for (const [index, item] of items.entries()) {
                const t =
                    exitAt === null
                        ? 0
                        : Math.min(
                              0.8,
                              (Math.max(0, out - index * TIMING.itemStaggerMs) /
                                  TIMING.itemsOutMs) *
                                  0.8,
                          );
                item.style.setProperty(
                    "--reward-item-y",
                    String(
                        reducedMotion.matches
                            ? 0
                            : -sampleRewardCurve(REWARD_CURVES.exitItemY, t),
                    ),
                );
                item.style.setProperty(
                    "--reward-glow-y",
                    String(
                        reducedMotion.matches
                            ? 0
                            : -sampleRewardCurve(REWARD_CURVES.exitGlowY, t),
                    ),
                );
                item.style.setProperty(
                    "--reward-item-opacity",
                    String(
                        reducedMotion.matches && exitAt !== null
                            ? 0
                            : sampleRewardCurve(REWARD_CURVES.exitOpacity, t),
                    ),
                );
            }
            const nextPhase: RewardCollectionPhase =
                exitAt !== null
                    ? out >= exitDuration + TIMING.backdropOutMs
                        ? "closed"
                        : "exiting"
                    : elapsed >= enteredAt
                      ? "visible"
                      : "entering";
            if (nextPhase !== currentPhase) {
                currentPhase = nextPhase;
                setPhase(nextPhase);
            }
            if (nextPhase !== "closed")
                frameId = requestAnimationFrame(renderFrame);
        };
        renderFrame(previous);
        document.addEventListener("visibilitychange", resetFrameTime);
        return () => {
            cancelAnimationFrame(frameId);
            document.removeEventListener("visibilitychange", resetFrameTime);
        };
    }, [collect, rewardCount, timeout]);

    useEffect(() => {
        if (
            phase !== "closed" ||
            requestedReason.current === null ||
            completionDelivered.current
        )
            return;
        completionDelivered.current = true;
        onCompleteRef.current(requestedReason.current);
    }, [phase]);
    return { bodyRef, phase, collect };
}
